import { Injectable } from '@angular/core';
import { BehaviorSubject, ReplaySubject, Observable, Subject, of as observableOf } from 'rxjs';
import { environment } from '../../../environments/environment';
import { CognitoUserPool, CognitoUserSession, CognitoUser } from 'amazon-cognito-identity-js';
import { CognitoIdentityCredentials, config } from 'aws-sdk';
import { User } from '../../model';
import * as _ from 'lodash'

@Injectable({
  providedIn: 'root'
})
export class AuthService {

  private _whenLoggedIn = new BehaviorSubject<User>(null);
  private _userPool: CognitoUserPool;
  private _user: User;
  private _selectedConsolidator:string = null;

  get whenLoggedIn(){ return this._whenLoggedIn };
  get user(){ return this._user }
  get userPool(){ return this._userPool }
  get selectedConsolidator(){ return this._selectedConsolidator}

  constructor(
  ) {
    this._userPool = new CognitoUserPool({
      UserPoolId: environment.cognitoUserPoolId,
      ClientId: environment.cognitoAppClientId
    });
    
    this.initAWSCredentials();
  }

  private setUser(user: User){
    this._user = user
    this._whenLoggedIn.next(user);
  }

  public setSelectedConsolidator(consolidator:string){
    //workround
    let code = consolidator.replace('"','')
    code = code.replace('"','')
    this._selectedConsolidator = code;
  }

  public initAWSCredentials(){
    let cognitoUser = this.userPool.getCurrentUser();
    //console.log(cognitoUser)

    if(!cognitoUser){
      return;
    }

    this.setUser(new User(cognitoUser));

    cognitoUser.getSession(async (err, session: CognitoUserSession) => {
      if(err){
        return console.log(err);
      }
      let logins = {
        [environment.cognitoIdentityEndpoint]: session.getIdToken().getJwtToken()
      };
  
      let creds = new CognitoIdentityCredentials({
        IdentityPoolId: environment.cognitoIdentityPoolId,
        Logins: logins
      },{
        region: environment.region
      });
  
      config.credentials = creds;

      await ((config.credentials) as CognitoIdentityCredentials).refreshPromise();

      if (!environment.production) {
        console.warn("token:", session.getIdToken().getJwtToken())
      }
      
    });
  }

  public checkAWSCredentials(callback: (err?) => void){
    let cognitoUser = this.userPool.getCurrentUser();
  
    if(!cognitoUser){
      return callback(true);
    }

    if(((config.credentials) as CognitoIdentityCredentials).needsRefresh()){
  
      cognitoUser.getSession((err, session: CognitoUserSession) => {
        if(err){
          console.log(err);
          return callback(err);
        }

        cognitoUser.refreshSession(session.getRefreshToken(), (err, session: CognitoUserSession) => {
          if(err){
            console.log(err);
            return callback(err);
          }
          
          let logins = {
            [environment.cognitoIdentityEndpoint]: session.getIdToken().getJwtToken()
          };
      
          let creds = new CognitoIdentityCredentials({
            IdentityPoolId: environment.cognitoIdentityPoolId,
            Logins: logins
          },{
            region: environment.region
          });
      
          config.credentials = creds;
    
          ((config.credentials) as CognitoIdentityCredentials).refresh((err) => {
            if(err){
              console.log(err);
              return callback(err);
            }
            callback();
          });
        });
        
      });
    } else {
      callback();
    }
  }

  public logout(){
    let cognitoUser = this.userPool.getCurrentUser();
    if(cognitoUser){
      cognitoUser.signOut();
    }

    // Clear Cognito Cache ID and re-initialize
    if (config.credentials) {
      ((config.credentials) as CognitoIdentityCredentials).clearCachedId();
      let creds = new CognitoIdentityCredentials({
        IdentityPoolId: environment.cognitoIdentityPoolId
      },{
        region: environment.region
      });      
      config.credentials = creds;
    }

    this.setUser(null);
    this.clearSettings();
  }

  public clearSettings() {
    const reinstate = sessionStorage.getItem('initView') == "1";

    sessionStorage.clear();
    localStorage.clear();

    // we reinstate is the previous value is "1"
    if (reinstate) {
      sessionStorage.setItem('initView', "1")
    }
  }

  // for now local storage. was thinking making this fetch everytime the application is loaded/reloaded.
  public storeSettings(data:any){
    // console.info("settings from login", data)

    let userDetails = data.success.results.user_details;
    let modulePermission = data.success.results.module_permissions.map(i=>i.permission_code);
    localStorage.setItem('name',userDetails.description);
    localStorage.setItem('modules', modulePermission);
    localStorage.setItem('low_level', userDetails.is_support);
  }

  public hasPermission(permissions: string | string[]) {
    if (_.isEmpty(permissions) || (_.isArray(permissions) && permissions.length == 0)) {
      return true;
    }

    if (_.isArray(permissions)) {
      return _.isEmpty(_.difference(permissions, this.user.permissions));
    } else {
      return _.indexOf(this.user?.permissions, permissions) >= 0;
    }
  }

  public isLogInAllowed(): boolean{
    return environment.allowLogin
  }

}
